/**
 * Feedback / Support Board Entity Contracts
 *
 * The projection a school's admin-only, Linear-backed support board speaks.
 * Linear is read live (no DB mirror — KTD1); the backend projector traverses
 * a school's customer → needs → linked issue and buckets each card. Comments
 * post to / read back from the linked Linear issue (KTD4).
 *
 * Display model (plan 2026-07-07-002, Revision 2026-07-08 restructure):
 * - The COLUMN KEY enum is the stable wire contract. The Linear
 *   `WorkflowState.type` → bucket mapping lives in the BACKEND projector
 *   (board/use-case.ts), never on the wire. Mapping:
 *     triage    → inbox        (the standalone Inbox page, NOT a kanban column)
 *     started   → in_progress
 *     completed → done
 *     backlog   → planned
 *     unstarted → planned
 *     canceled  → HIDDEN (dropped — not shown anywhere)
 *     unrecognized state → planned
 *   A need with NO linked issue (project-linked scaffolding) is dropped entirely.
 * - The board splits into a three-column kanban (`planned`, `in_progress`,
 *   `done`) plus a separate flat `inbox` card list the Inbox page renders as a
 *   data table.
 * - Every card carries a `source` ("call" | "parent" | "teacher" | null) —
 *   derived backend-side from the linked issue's "Source" label group — so the
 *   board can badge where a request came from.
 * - Every card also carries a `type` ("defect" | "improvement" | "feature" |
 *   null) — derived backend-side from the linked issue's type label, matched by
 *   id — so the school can see what kind of request it was logged as.
 *   (plan 2026-08-22-1718 school-support-visibility)
 * - `authorKind` (`customer`|`team`) is derived backend-side from Linear's
 *   `botActor` (app-posted = customer) vs `user` (team) on read-back (KTD4/KTD5);
 *   the wire carries the resolved kind, not the raw Linear actor.
 *
 * See: docs/plans/2026-07-07-002-feature-support-board-intake-states-plan.md
 *      docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-002-feat-customer-feedback-roadmap-plan.md (origin)
 */

import { z } from 'zod'

// ---------------------------------------------------------------------------
// Column key — the stable wire contract
// ---------------------------------------------------------------------------

/**
 * Board column keys, in canonical display order. The durable wire contract; the
 * Linear `state.type` → column map lives in the backend projector, not here.
 *
 * - `inbox`       — freshly-submitted intake (Linear Triage). Carried in the
 *                   response's separate `inbox` list (rendered on its own Inbox
 *                   page), NOT one of the kanban columns.
 * - `planned`     — accepted but not started (backlog / unstarted).
 * - `in_progress` — actively being worked (started).
 * - `done`        — completed.
 *
 * `inbox` stays in the enum because inbox cards still carry `column: 'inbox'`;
 * the three kanban columns are `planned`, `in_progress`, `done`.
 */
export const FeedbackColumnKeySchema = z.enum(['inbox', 'planned', 'in_progress', 'done'])
export type FeedbackColumnKey = z.infer<typeof FeedbackColumnKeySchema>

// ---------------------------------------------------------------------------
// Source — where the request came from
// ---------------------------------------------------------------------------

/**
 * Where a request originated, derived backend-side from the linked issue's
 * "Source" label group:
 * - `call`    — raised by the school on a call (logged by the SJ team).
 * - `parent`  — a parent submitted it through the mobile app.
 * - `teacher` — a teacher/faculty member submitted it through the mobile app.
 * Null when the issue carries no Source-group label.
 */
export const FeedbackCardSourceSchema = z.enum(['call', 'parent', 'teacher'])
export type FeedbackCardSource = z.infer<typeof FeedbackCardSourceSchema>

// ---------------------------------------------------------------------------
// Type — what kind of request it is
// ---------------------------------------------------------------------------

/**
 * What kind of request this is, derived backend-side from the linked issue's
 * type label matched by id (KTD1):
 * - `defect`      — something is broken.
 * - `improvement` — something existing should work better.
 * - `feature`     — something new.
 *
 * These three are the canonical vocabulary (KD2); the school sees them, not the
 * submission form's wording (KD4 — the mobile form's "Report a bug" is a
 * `defect` here). The legacy `bug` label normalizes to `defect` in the backend
 * projector (KTD2/R11), so the wire never carries a legacy value. Null when the
 * issue carries no recognized type label (KTD8), including requests created
 * before the contract carried type — a null renders no badge rather than a
 * guessed one.
 */
export const FeedbackCardTypeSchema = z.enum(['defect', 'improvement', 'feature'])
export type FeedbackCardType = z.infer<typeof FeedbackCardTypeSchema>

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

/**
 * A single board card — one Linear customer need, projected with its linked
 * issue's display fields. The verbatim request body is passed through
 * unscrubbed (R11). Every card now has a linked issue (needs with no issue are
 * dropped by the projector), so the issue fields are always populated; the
 * nullable shape is retained defensively.
 */
export const FeedbackCardSchema = z.object({
  /** Linear CustomerNeed id. Stable identifier for the card. */
  id: z.string(),
  /** Verbatim customer request (need `body`/`content`). Passed through unscrubbed (R11). */
  requestBody: z.string(),
  /** Whether this need has a linked Linear issue. False → issue fields are null. */
  hasLinkedIssue: z.boolean(),
  /** Linked issue id (Linear issue id), null when unlinked. Used to fetch/post comments. */
  issueId: z.string().nullable(),
  /** Human-readable issue identifier (e.g. "SCH-225"), null when unlinked. */
  issueIdentifier: z.string().nullable(),
  /** Linked issue title (R6), null when unlinked. */
  issueTitle: z.string().nullable(),
  /**
   * Linked issue priority as Linear's numeric scale (0 No priority, 1 Urgent,
   * 2 High, 3 Normal/Medium, 4 Low). Null when unlinked. Surfaced on the card (R10).
   */
  issuePriority: z.number().int().min(0).max(4).nullable(),
  /**
   * The linked issue's "Area" label-group values (module: mobile/web/backend/
   * devops/marketing). Empty array when unlinked or untagged. Shown on the card
   * in place of priority.
   */
  issueAreas: z.array(z.string()).default([]),
  /**
   * Number of comments on the linked issue's thread (both customer- and
   * team-side), bounded by the projection's page size. 0 when unlinked or
   * when the thread is empty — the card hides its comment bubble at 0.
   */
  commentCount: z.number().int().min(0).default(0),
  /**
   * What kind of request this is (R1/R10) — one of the three canonical types,
   * or null when the linked issue carries no recognized type label. Rendered as
   * the card's type badge; a null renders no badge at all (KTD8).
   */
  type: FeedbackCardTypeSchema.nullable().default(null),
  /**
   * Where the request came from (Source-group label), or null when untagged.
   * Rendered as a small origin badge on the card.
   */
  source: FeedbackCardSourceSchema.nullable().default(null),
  /**
   * True when the linked issue carries the `waiting-on-school` ownership label.
   * Still projected (harmless), but no longer drives a board bucket after the
   * 2026-07-08 restructure removed the `needs_input` column.
   */
  awaitingSchool: z.boolean().default(false),
  /**
   * The linked issue's workflow-state display name (e.g. "In Review",
   * "In Progress"). A subtle status hint; null when unlinked.
   */
  statusLabel: z.string().nullable().default(null),
  /**
   * ISO 8601 timestamp the request was submitted (the Linear customer need's
   * `createdAt`). Null when absent. Distinct from `updatedAt`: the detail view
   * shows both, so a reader can tell a long-open request from a busy one (R8).
   */
  createdAt: z.string().nullable().default(null),
  /**
   * ISO 8601 timestamp the request last moved (linked issue `updatedAt`, else
   * the need `createdAt`). Null when neither is available. Drives the card's
   * "last updated" line.
   */
  updatedAt: z.string().nullable().default(null),
  /** Resolved board column for this card. */
  column: FeedbackColumnKeySchema
})
export type FeedbackCard = z.infer<typeof FeedbackCardSchema>

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------

/** One board column: its stable key, a display label, and its cards. */
export const FeedbackColumnSchema = z.object({
  /** Stable column key (the wire contract). */
  key: FeedbackColumnKeySchema,
  /** Display label (e.g. "Planned", "In progress", "Done"). Backend-supplied. */
  label: z.string(),
  /** Cards bucketed into this column. */
  cards: z.array(FeedbackCardSchema)
})
export type FeedbackColumn = z.infer<typeof FeedbackColumnSchema>

// ---------------------------------------------------------------------------
// Comment
// ---------------------------------------------------------------------------

/** Who authored a comment, resolved backend-side from Linear's botActor vs user (KTD4). */
export const FeedbackCommentAuthorKindSchema = z.enum(['customer', 'team'])
export type FeedbackCommentAuthorKind = z.infer<typeof FeedbackCommentAuthorKindSchema>

/**
 * A single comment on a linked issue's thread. `authorKind` distinguishes
 * customer-side (app-posted, `botActor`) from team-side (`user`) replies (R13);
 * `authorLabel` is a display string (school label or team member name).
 */
export const FeedbackCommentSchema = z.object({
  /** Linear comment id. */
  id: z.string(),
  /** Comment body (verbatim; not scrubbed). */
  body: z.string(),
  /** ISO 8601 timestamp the comment was created. */
  createdAt: z.string(),
  /** Customer-side vs team-side authorship (R13). */
  authorKind: FeedbackCommentAuthorKindSchema,
  /** Display label for the author (school label or team member name). */
  authorLabel: z.string()
})
export type FeedbackComment = z.infer<typeof FeedbackCommentSchema>
